/* Natua · carrusel de videos de la página de producto (bloque "Carrusel de videos").
   Se carga con defer y solo cuando el bloque tiene videos (snippets/natua-video-carousel.liquid). Sin dependencias.
   · Infinito: las diapositivas forman un anillo; con menos de 5 videos se clonan (2 → 6, 3 → 6, 4 → 8) para que siempre haya
     vecinos a ambos lados y el deslizamiento se vea continuo. Con 1 video no hay anillo, flechas ni puntos.
   · Solo cambia data-pos de cada diapositiva (0 centro, -1/1 vecinos, -2/2 ocultas); el CSS hace el movimiento con transform y opacity.
   · Deslizar con pointer events (touch y mouse), flechas del teclado con el carrusel enfocado, botones de flecha y tocar un vecino.
   · Rendimiento: los videos traen preload="none" y no tienen autoplay; aquí se reproduce SOLO el video central, y SOLO mientras el
     carrusel está a la vista (IntersectionObserver) y la pestaña está visible. Con movimiento reducido o ahorro de datos no hay
     reproducción automática: el video arranca cuando la persona lo pide. */
(() => {
  if (!('customElements' in window) || customElements.get('natua-video-carousel')) return;

  const MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const MIN_RING = 5; // diapositivas distintas para ver 2 a cada lado + la central sin que una "cruce" la pantalla
  const SHOWN = 0.4; // parte del carrusel que debe verse para que el video central se reproduzca
  const SWIPE_MIN = 8; // píxeles de movimiento horizontal para saber que es un arrastre
  const SWIPE_DISTANCE = 60; // píxeles (como máximo) para cambiar de video al soltar; si arrastró menos, vuelve
  const SWIPE_SPEED = 0.4; // píxeles por milisegundo: un deslizamiento rápido también cambia de video

  const autoplayAllowed = () =>
    !MOTION.matches && !(connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '')));

  class NatuaVideoCarousel extends HTMLElement {
    connectedCallback() {
      if (this.nReady) return;
      if (!this.querySelector('[data-n-vc-track]')) {
        // el navegador puede conectar el elemento antes de leer sus hijos
        document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
        return;
      }
      this.nReady = true;
      this.setup();
    }

    disconnectedCallback() {
      this.teardown();
    }

    setup() {
      this.viewport = this.querySelector('[data-n-vc-viewport]');
      this.track = this.querySelector('[data-n-vc-track]');
      this.controls = this.querySelector('[data-n-vc-controls]');
      this.playButton = this.querySelector('[data-n-vc-play]');
      this.soundButton = this.querySelector('[data-n-vc-sound]');
      this.live = this.querySelector('[data-n-vc-live]');
      this.dots = Array.from(this.querySelectorAll('[data-n-vc-dots] .n-vc__dot'));

      const originals = Array.from(this.track.children);
      this.total = originals.length;
      if (!this.total) {
        this.nReady = false;
        return;
      }

      // Anillo de diapositivas (los clones repiten los videos en el mismo orden: el índice real es posición % total)
      this.slides = originals.slice();
      if (this.total > 1 && this.total < MIN_RING) {
        const copies = Math.ceil(MIN_RING / this.total);
        for (let copy = 1; copy < copies; copy++) {
          originals.forEach((slide) => {
            const clone = slide.cloneNode(true);
            clone.setAttribute('data-clone', '');
            this.track.appendChild(clone);
            this.slides.push(clone);
          });
        }
      }
      this.size = this.slides.length;
      this.half = Math.floor(this.size / 2);

      this.a = 0; // posición activa en el anillo
      this.inView = false;
      this.userPaused = false; // la persona pausó el video central (sigue pausado hasta que cambie de video)
      this.userPlay = false; // la persona pidió reproducir (solo importa con movimiento reducido o ahorro de datos)
      this.locked = false;
      this.drag = null;
      this.dragEnd = -1000;

      this.abort = new AbortController();
      const on = (target, type, handler, options) =>
        target.addEventListener(type, handler, Object.assign({ signal: this.abort.signal }, options));

      this.videos = this.slides.map((slide) => slide.querySelector('video'));
      this.videos.forEach((video) => {
        if (!video) return;
        // Un clon no hereda el estado "sin sonido" del atributo: se fija aquí en todos
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.controls = false;
        video.disablePictureInPicture = true; // sin el botón flotante de imagen en imagen de Edge/Chrome ni el de transmitir
        video.disableRemotePlayback = true;
        video.removeAttribute('autoplay');
        ['play', 'pause', 'volumechange'].forEach((type) =>
          on(video, type, () => {
            if (video === this.videos[this.a]) this.updateControls();
          })
        );
      });

      this.render(true);
      this.updateMeta(false);
      this.updateControls();

      // Interacción
      on(this.viewport, 'click', (event) => this.onClick(event));
      on(this.playButton, 'click', () => this.togglePlay());
      on(this.soundButton, 'click', () => this.toggleSound());
      if (this.total > 1) {
        on(this.viewport, 'pointerdown', (event) => this.onDown(event));
        on(this.viewport, 'pointermove', (event) => this.onMove(event));
        on(this.viewport, 'pointerup', (event) => this.onUp(event));
        on(this.viewport, 'pointercancel', (event) => this.onUp(event));
        on(this.viewport, 'dragstart', (event) => event.preventDefault());
        on(this, 'keydown', (event) => this.onKey(event));
        const prev = this.querySelector('[data-n-vc-prev]');
        const next = this.querySelector('[data-n-vc-next]');
        if (prev) on(prev, 'click', () => this.move(-1));
        if (next) on(next, 'click', () => this.move(1));
      }

      // Reproducción: solo con el carrusel a la vista y la pestaña visible
      on(document, 'visibilitychange', () => {
        if (document.hidden) this.mute();
        this.sync();
      });
      const onMotion = () => this.sync();
      if (MOTION.addEventListener) on(MOTION, 'change', onMotion);
      else if (MOTION.addListener) {
        MOTION.addListener(onMotion);
        this.motionOff = () => MOTION.removeListener(onMotion);
      }
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            const entry = entries[entries.length - 1];
            const visible = entry.isIntersecting && entry.intersectionRatio >= SHOWN;
            if (visible === this.inView) return;
            this.inView = visible;
            if (!visible) {
              this.userPlay = false;
              this.mute();
            }
            this.sync();
          },
          { threshold: [0, SHOWN] }
        );
        this.io.observe(this.viewport);
      }

      // Los controles que necesitan JavaScript aparecen recién ahora (sin JS no hay botones que no hagan nada)
      this.querySelectorAll('[data-n-vc-prev], [data-n-vc-next], [data-n-vc-controls]').forEach((element) => {
        element.hidden = false;
      });
    }

    teardown() {
      if (!this.nReady) return;
      this.nReady = false;
      clearTimeout(this.lockTimer);
      if (this.abort) this.abort.abort();
      if (this.io) this.io.disconnect();
      if (this.motionOff) this.motionOff();
      (this.videos || []).forEach((video) => video && video.pause());
      this.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());
    }

    /* ---------- Anillo ---------- */
    render(initial) {
      this.slides.forEach((slide, r) => {
        // d = distancia con signo desde la diapositiva activa, dentro del anillo
        const d = ((((r - this.a + this.half) % this.size) + this.size) % this.size) - this.half;
        const pos = d === 0 ? '0' : d === -1 ? '-1' : d === 1 ? '1' : d < 0 ? '-2' : '2';
        const before = slide.getAttribute('data-pos');
        if (before !== pos) {
          // La que pasa de "oculta a la izquierda" a "oculta a la derecha" (o al revés) salta sin animar: nadie la ve cruzar
          const jumps = initial || (before === '-2' && pos === '2') || (before === '2' && pos === '-2');
          if (jumps) slide.classList.add('is-jump');
          slide.setAttribute('data-pos', pos);
          if (jumps) {
            void slide.offsetWidth; // aplica el salto sin transición antes de quitar la clase
            requestAnimationFrame(() => slide.classList.remove('is-jump'));
          }
        }
        if (r === this.a) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
    }

    // Separación entre centros, en anchos de video: es la misma variable que usa el CSS (--n-vc-shift: 92%)
    shift() {
      const value = parseFloat(getComputedStyle(this).getPropertyValue('--n-vc-shift')) / 100;
      return value > 0 ? value : 0.92;
    }

    duration() {
      const value = getComputedStyle(this.slides[this.a]).transitionDuration || '0s';
      return Math.max(0, ...value.split(',').map((part) => (part.trim().endsWith('ms') ? parseFloat(part) : parseFloat(part) * 1000) || 0));
    }

    move(step) {
      if (this.total < 2 || this.locked) return;
      this.setActive((this.a + step + this.size) % this.size);
    }

    setActive(index) {
      const previous = this.videos[this.a];
      if (previous) {
        previous.pause();
        previous.muted = true;
        if (previous.readyState > 0) previous.currentTime = 0;
      }
      this.a = index;
      this.userPaused = false;
      this.userPlay = false;
      this.render(false);
      this.updateMeta(true);
      this.sync();
      const ms = this.duration();
      if (ms > 0) {
        // Mientras se desliza no se acepta otro movimiento: así ninguna diapositiva salta a la vista a medias
        this.locked = true;
        clearTimeout(this.lockTimer);
        this.lockTimer = setTimeout(() => {
          this.locked = false;
        }, ms);
      }
    }

    updateMeta(announce) {
      const index = this.a % this.total;
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      if (announce && this.live) this.live.textContent = this.slides[this.a].getAttribute('aria-label') || '';
    }

    /* ---------- Reproducción ---------- */
    // El sonido solo está activo mientras la persona mira el video: al salir de la vista o de la pestaña vuelve a apagado
    mute() {
      const video = this.videos[this.a];
      if (video && !video.muted) video.muted = true;
    }

    sync() {
      const active = this.videos[this.a];
      this.videos.forEach((video, r) => {
        if (video && r !== this.a && !video.paused) video.pause();
      });
      if (!active) return;
      const wants = this.inView && !document.hidden && (autoplayAllowed() ? !this.userPaused : this.userPlay);
      if (wants && active.paused) this.start(active);
      else if (!wants && !active.paused) active.pause();
      this.updateControls();
    }

    start(video) {
      const result = video.play();
      if (result && result.catch) {
        result.catch(() => {
          // El navegador no dejó reproducir (ahorro de batería, política de autoplay): queda el póster y el botón de reproducir
          this.updateControls();
        });
      }
    }

    updateControls() {
      const video = this.videos[this.a];
      const playing = Boolean(video) && !video.paused && !video.ended;
      if (this.controls) this.controls.classList.toggle('is-playing', playing);
      if (this.playButton) {
        const label = playing ? this.playButton.dataset.labelPause : this.playButton.dataset.labelPlay;
        if (label) this.playButton.setAttribute('aria-label', label);
      }
      if (this.soundButton) this.soundButton.setAttribute('aria-pressed', String(Boolean(video) && !video.muted));
    }

    togglePlay() {
      const video = this.videos[this.a];
      if (!video) return;
      if (video.paused) {
        this.userPaused = false;
        this.userPlay = true;
        this.start(video);
      } else {
        this.userPaused = true;
        this.userPlay = false;
        video.pause();
      }
      this.updateControls();
    }

    // Tocar el video (o el botón de sonido): activa o quita el sonido; si estaba pausado, lo reproduce con sonido
    toggleSound() {
      const video = this.videos[this.a];
      if (!video) return;
      if (video.paused) {
        this.userPaused = false;
        this.userPlay = true;
        video.muted = false;
        const result = video.play();
        if (result && result.catch) {
          result.catch(() => {
            video.muted = true;
            this.start(video);
          });
        }
      } else {
        video.muted = !video.muted;
      }
      this.updateControls();
    }

    /* ---------- Gestos ---------- */
    onClick(event) {
      if (performance.now() - this.dragEnd < 350) return; // el "click" que sigue a un arrastre no cuenta
      const rect = this.slides[this.a].getBoundingClientRect();
      if (event.clientY < rect.top || event.clientY > rect.bottom) return;
      if (event.clientX < rect.left) {
        this.move(-1);
      } else if (event.clientX > rect.right) {
        this.move(1);
      } else {
        this.toggleSound();
      }
    }

    onDown(event) {
      if (this.locked || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.drag = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        time: performance.now(),
        step: this.slides[this.a].offsetWidth * this.shift(),
        active: false,
      };
    }

    onMove(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
          this.drag = null; // es scroll vertical: el navegador se encarga
          return;
        }
        if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy)) return;
        drag.active = true;
        try {
          this.viewport.setPointerCapture(event.pointerId);
        } catch (error) {
          /* sin captura el arrastre sigue funcionando dentro del carrusel */
        }
        this.viewport.classList.add('is-dragging');
      }
      // Las diapositivas siguen al dedo, sin pasar de un paso
      this.track.style.setProperty('--n-vc-drag', Math.max(-drag.step, Math.min(drag.step, dx)) + 'px');
    }

    onUp(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      this.drag = null;
      if (!drag.active) return;
      this.viewport.classList.remove('is-dragging');
      this.track.style.removeProperty('--n-vc-drag'); // vuelve a 0 con transición (o se completa el paso)
      this.dragEnd = performance.now();
      if (event.type === 'pointercancel') return;
      const dx = event.clientX - drag.x;
      const speed = Math.abs(dx) / Math.max(performance.now() - drag.time, 1);
      if (Math.abs(dx) > Math.min(SWIPE_DISTANCE, drag.step * 0.3) || (speed > SWIPE_SPEED && Math.abs(dx) > 24)) {
        this.move(dx < 0 ? 1 : -1);
      }
    }

    onKey(event) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(1);
      }
    }
  }

  customElements.define('natua-video-carousel', NatuaVideoCarousel);
})();
